/**
 * shared/rules.js — bộ đánh giá luật, dùng chung cho rubric chấm điểm (#2) và
 * khớp khẩu vị quỹ (#1).
 *
 * Vì sao tách riêng: rubric và khẩu vị quỹ đều nằm trong Firestore dưới dạng dữ
 * liệu, không phải code. Muốn vậy phải có một ngôn ngữ luật nhỏ, cố định, mà cả
 * hai cùng nói. Thêm tiêu chí mới = thêm một object, không phải sửa engine.
 *
 * Một luật trông như:
 *   { path: 'team.founders', op: 'someTrue', field: 'fullTime' }
 *   { path: 'derived.runwayMonths', op: 'gte', value: 9 }
 *   { path: 'business.industries', op: 'intersects', value: ['FINTECH', 'AI'] }
 *
 * ─────────────────────────────────────────────────────────────────────────
 * PHÂN BIỆT QUAN TRỌNG: "sai" khác "chưa có thông tin".
 *
 * Một tiêu chí không đạt vì doanh nghiệp thực sự chưa làm, và một tiêu chí
 * không đạt vì người dùng chưa điền ô đó — hai chuyện hoàn toàn khác nhau, và
 * lời khuyên đưa ra cũng phải khác. Vì thế mọi hàm ở đây trả về cả `dat` lẫn
 * `coThongTin`.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { toVnd } from './money.js';

/** Lấy giá trị theo đường dẫn kiểu 'a.b.c'. Trả undefined nếu đứt giữa chừng. */
export function layGiaTri(obj, path) {
  if (!path) return undefined;
  let cur = obj;
  for (const khoa of String(path).split('.')) {
    if (cur == null) return undefined;
    cur = cur[khoa];
  }
  return cur;
}

const soHoa = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'object' && 'amount' in v) return toVnd(v);
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const laMang = Array.isArray;

/**
 * Các phép so sánh được phép. Danh sách này CỐ TÌNH ngắn.
 * Mỗi phép mới thêm vào là một thứ phải hỗ trợ mãi mãi, vì rubric cũ đã
 * publish sẽ tiếp tục dùng nó để tính lại điểm cũ.
 */
const PHEP = {
  exists: (gt) => gt != null,
  notEmpty: (gt) => {
    if (gt == null) return false;
    if (laMang(gt)) return gt.length > 0;
    if (typeof gt === 'string') return gt.trim().length > 0;
    return true;
  },
  isTrue: (gt) => gt === true,
  isFalse: (gt) => gt === false,
  equals: (gt, r) => gt === r.value,
  notEquals: (gt, r) => gt !== r.value,
  in: (gt, r) => laMang(r.value) && r.value.includes(gt),
  notIn: (gt, r) => laMang(r.value) && !r.value.includes(gt),

  gte: (gt, r) => soHoa(gt) != null && soHoa(gt) >= soHoa(r.value),
  gt: (gt, r) => soHoa(gt) != null && soHoa(gt) > soHoa(r.value),
  lte: (gt, r) => soHoa(gt) != null && soHoa(gt) <= soHoa(r.value),
  lt: (gt, r) => soHoa(gt) != null && soHoa(gt) < soHoa(r.value),

  lengthGte: (gt, r) => typeof gt === 'string' && gt.trim().length >= Number(r.value),
  countGte: (gt, r) => laMang(gt) && gt.length >= Number(r.value),

  /** Mảng có ít nhất một phần tử mà `field` bằng true. */
  someTrue: (gt, r) => laMang(gt) && gt.some((x) => layGiaTri(x, r.field) === true),
  everyTrue: (gt, r) => laMang(gt) && gt.length > 0
    && gt.every((x) => layGiaTri(x, r.field) === true),
  everyFalse: (gt, r) => laMang(gt) && gt.length > 0
    && gt.every((x) => layGiaTri(x, r.field) !== true),

  /** Mảng có ít nhất `count` phần tử thoả một so sánh con trên `field`. */
  someMatch: (gt, r) => {
    if (!laMang(gt)) return false;
    const con = PHEP[r.subOp];
    if (!con) throw new Error(`Không có phép so sánh: ${r.subOp}`);
    const dem = gt.filter((x) => con(layGiaTri(x, r.field), { value: r.value })).length;
    return dem >= (r.count ?? 1);
  },

  /** Hai tập hợp có phần chung — dùng cho khớp ngành, giai đoạn với quỹ. */
  intersects: (gt, r) => laMang(gt) && laMang(r.value) && gt.some((x) => r.value.includes(x)),
  /** Tập giá trị nằm trọn trong tập cho phép. */
  subsetOf: (gt, r) => laMang(gt) && laMang(r.value) && gt.every((x) => r.value.includes(x)),
};

export const PHEP_HOP_LE = Object.freeze(Object.keys(PHEP));

/**
 * Chạy một luật trên ngữ cảnh.
 * @returns {{ dat: boolean, coThongTin: boolean, giaTri: * }}
 */
export function apDung(rule, ctx) {
  if (!rule || !rule.op) throw new Error('Luật phải có op');
  const ham = PHEP[rule.op];
  if (!ham) throw new Error(`Không có phép so sánh: ${rule.op}`);

  const giaTri = layGiaTri(ctx, rule.path);

  // undefined / null = chưa có thông tin. Mảng rỗng thì KHÁC: đó là một câu trả
  // lời ("chưa có bằng chứng nào"), nên vẫn coi là có thông tin và cho trượt.
  const coThongTin = giaTri != null;

  if (!coThongTin) {
    // Vài phép có nghĩa ngay cả khi thiếu giá trị.
    if (rule.op === 'exists' || rule.op === 'notEmpty' || rule.op === 'isFalse') {
      return { dat: ham(giaTri, rule), coThongTin: false, giaTri };
    }
    return { dat: false, coThongTin: false, giaTri };
  }

  return { dat: ham(giaTri, rule), coThongTin: true, giaTri };
}

/**
 * Thang điểm bậc thang: [{ gte: 3, points: 8 }, { gte: 1, points: 4 }].
 * Duyệt từ trên xuống, lấy bậc đầu tiên đạt. Cho phép chấm có mức độ thay vì
 * chỉ đạt/không đạt — thực tế mức độ sẵn sàng không bao giờ nhị phân.
 */
export function chamThang(thang, giaTri) {
  const n = soHoa(giaTri);
  if (n == null) return { points: 0, coThongTin: false, bac: null };
  for (const bac of thang) {
    if (n >= Number(bac.gte)) return { points: bac.points, coThongTin: true, bac };
  }
  return { points: 0, coThongTin: true, bac: null };
}
